import {
  Modal,
  Button,
  Input,
  Form,
  Select,
  Row,
} from "antd";
import React, { useEffect, useState } from "react";
import { getCurrentDateTimeInFormat } from "../../../helper/date_helper";
import { getVendors, testConnection } from "../../../service/db.service";
import { showNotice } from "../../../helper/notify";

// Fallback only — the list is served from VendorRegistry so adding a vendor is one class.
const FALLBACK_CONNECTION_LIST = [
  { value: "sqlserver", label: "SQL Server" },
  { value: "pgsql", label: "PostgreSQL" },
];

// Snowflake takes warehouse/role/schema as URL query parameters rather than as separate
// connection mechanics, so they ride in the free-form extraParams column.
const SNOWFLAKE = "snowflake";
const KEYPAIR = "keypair";
const SNOWFLAKE_FIELDS = [
  { name: "warehouse", label: "Warehouse", placeholder: "e.g. COMPUTE_WH" },
  { name: "role", label: "Role", placeholder: "e.g. ARC_READER" },
  { name: "schema", label: "Schema", placeholder: "e.g. PUBLIC" },
];

// The private key path travels in extra_params alongside the three fields above. A PATH is
// not a secret — the key itself stays a file on the ARC server under its own ACLs — so it
// is stored in the clear on purpose. The optional passphrase is what gets encrypted.
const PRIVATE_KEY_FIELD = "privateKeyFile";
const PRIVATE_KEY_PARAM = "private_key_file";

// Authentication options per database type. Windows Signon and SQL authentication are
// SQL Server concepts and mean nothing to Snowflake, so Snowflake gets its own pair rather
// than inheriting a list on which both entries are wrong.
const AUTH_OPTIONS = {
  [SNOWFLAKE]: [
    { value: KEYPAIR, label: "Key pair" },
    { value: "password", label: "Password" },
  ],
  default: [
    { value: "windows", label: "Windows Signon" },
    { value: "sql", label: "SQL authentication" },
  ],
};
const authOptionsFor = (type) => AUTH_OPTIONS[type] || AUTH_OPTIONS.default;
function DbConnectionModal(props) {
  const [form] = Form.useForm();
  const [testConnectionResult, setTestConnectionResult] = useState(null);
  const [connectionList, setConnectionList] = useState(FALLBACK_CONNECTION_LIST);
  // Watched so the per-type field set re-renders as the user switches type.
  const databaseType = Form.useWatch("databaseType", form);
  const authenticationType = Form.useWatch("authenticationType", form);
  const isSnowflake = databaseType === SNOWFLAKE;
  // Key-pair replaces the password entirely: the passphrase field below is optional, since
  // the primary path is an unencrypted key protected by the server's own file permissions.
  const isKeypair = isSnowflake && authenticationType === KEYPAIR;

  useEffect(() => {
    getVendors()
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setConnectionList(res.data);
      })
      // Keep the hardcoded list if the endpoint is unavailable — an older backend must not
      // leave the form with no types at all.
      .catch(() => {});
  }, []);

  const onFinish = (formData) => {
    // Pack the Snowflake-only fields into the single free-form column. Non-Snowflake types
    // send undefined, so their stored row is byte-identical to before.
    const extraParams = formData.databaseType === SNOWFLAKE
      ? JSON.stringify(
          SNOWFLAKE_FIELDS.reduce((acc, f) => {
            if (formData[f.name]) acc[f.name] = formData[f.name];
            return acc;
          }, formData[PRIVATE_KEY_FIELD]
            ? { [PRIVATE_KEY_PARAM]: formData[PRIVATE_KEY_FIELD] }
            : {}),
        )
      : undefined;

    testConnection(
      formData.databaseType,
      formData.dbName,
      formData.hostName,
      formData.port,
      formData.username,
      formData.password,
      extraParams,
      // The backend checks the key file exists and is readable before it tries to connect;
      // the driver's own error for a missing key is an opaque authentication failure.
      formData.authenticationType
    ).then((res) => {
      console.log(res.data);
      if (res.data.success) {
        props.updateTable({ ...formData, extraParams });
        showNotice("success", "Valid connection", "");
        form.resetFields();
      } else {
        showNotice(
          "error",
          "Error connecting to DB!",
          res.data.messageDescription
        );
      }
    });
  };
  return (
    <div>
      <Modal
        open={props.isOpen}
        centered
        onCancel={() => props.onCancel()}
        footer={[
          <Button key="back" onClick={() => props.onCancel()}>
            Cancel
          </Button>,
          <Button form="myForm" key="submit" htmlType="submit" type="primary">
            Test & Add
          </Button>,
        ]}
        destroyOnClose
        width="700px"
      >
        <Row style={{ paddingBottom: "20px" }}>
          <p className="modal-header-text">New Database Connection</p>
        </Row>
        <Form
          id="myForm"
          form={form}
          onFinish={onFinish}
          labelCol={{
            span: 6,
          }}
          wrapperCol={{
            span: 24,
          }}
          layout="horizontal"
          style={{
            maxWidth: "100%",
          }}
          labelAlign="left"
        >
          <Form.Item
            label="Display name"
            name="displayName"
            style={{ width: "100%" }}
            rules={[{ required: true, message: "Please enter a display name" }]}
          >
            <Input placeholder="display name" />
          </Form.Item>
          <Form.Item
            label="Connection Type"
            name="databaseType"
            style={{ width: "100%" }}
            rules={[
              { required: true, message: "Please select a connection type" },
            ]}
          >
            <Select
              placeholder="connection type"
              options={connectionList}
              // The auth options differ per type, so a value chosen for the previous type
              // can be left selected but invalid — "Windows Signon" surviving a switch to
              // Snowflake would submit an auth type that list no longer offers.
              onChange={() => form.setFieldsValue({ authenticationType: undefined })}
            ></Select>
          </Form.Item>
          <Form.Item
            label="Database name"
            name="dbName"
            style={{ width: "100%" }}
            rules={[
              { required: true, message: "Please enter a database name" },
            ]}
          >
            <Input placeholder="database name" />
          </Form.Item>

          <Form.Item
            label={isSnowflake ? "Account Host" : "Host Name"}
            name="hostName"
            style={{ width: "100%" }}
            rules={[{ required: true, message: "Please enter a host name" }]}
          >
            <Input
              placeholder={
                isSnowflake
                  ? "orgname-accountname.snowflakecomputing.com"
                  : "hostname or server name"
              }
            />
          </Form.Item>
          {/* Snowflake defaults to 443 and takes no port in the URL. Hidden rather than
              disabled, and its required rule drops with it so it cannot block submit. */}
          {!isSnowflake && (
            <Form.Item
              label="Port"
              name="port"
              style={{ width: "100%" }}
              rules={[{ required: true, message: "Please enter a port" }]}
            >
              <Input placeholder="port" />
            </Form.Item>
          )}
          {isSnowflake &&
            SNOWFLAKE_FIELDS.map((f) => (
              <Form.Item
                key={f.name}
                label={f.label}
                name={f.name}
                style={{ width: "100%" }}
              >
                <Input placeholder={f.placeholder} />
              </Form.Item>
            ))}

          <Form.Item
            label="Authentication Type"
            name="authenticationType"
            style={{ width: "100%" }}
            rules={[
              { required: true, message: "Please select authentication type" },
            ]}
          >
            <Select
              placeholder="authentication type"
              options={authOptionsFor(databaseType)}
            />
          </Form.Item>
          <Form.Item
            label="Username"
            name="username"
            style={{ width: "100%" }}
            rules={[{ required: true, message: "Please enter a username" }]}
          >
            <Input placeholder="username" />
          </Form.Item>
          {isKeypair && (
            <Form.Item
              label="Private Key Path"
              name={PRIVATE_KEY_FIELD}
              style={{ width: "100%" }}
              rules={[
                { required: true, message: "Please enter the private key path" },
              ]}
              extra={
                "Path on the ARC SERVER, not on this machine. The file must be readable " +
                "only by the ARC service account — nothing verifies that automatically."
              }
            >
              <Input placeholder="C:\\ARC_APP\\keys\\snowflake_rsa_key.p8" />
            </Form.Item>
          )}
          {/* Optional for key pair: an unencrypted key is the primary path, so requiring a
              passphrase here would block it. Still required for every password-based type. */}
          <Form.Item
            label={isKeypair ? "Key Passphrase" : "Password"}
            name="password"
            style={{ width: "100%" }}
            required={!isKeypair}
            extra={
              isKeypair
                ? "Leave blank unless the private key itself is encrypted."
                : undefined
            }
          >
            <Input.Password
              placeholder={isKeypair ? "optional" : "password"}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default DbConnectionModal;
